/**
 * Arya's loader: the only chat script on a page view. Deferred, ~2 kB.
 *
 * The rest of the chat (copy, state, api, cards, chat) is fetched on the first
 * sign of intent, so a visitor who never touches it pays nothing
 * (docs/architecture/PUBLIC_PAGE_PERFORMANCE_OPTIMIZATION.md, "defer chatbot JS"):
 *
 *   - hover, focus, touch or press on the floating button;
 *   - a click on an "Ask Arya" button;
 *   - a chat a person is answering, carried over from the previous page;
 *   - the nudge: 30 s on a service page, or exit intent on a desktop.
 *
 * The script URLs come from `data-arya-scripts` on #floating-contact, written
 * with {% static %} so hashed production filenames resolve. Loaded in order as
 * classic scripts: no bundler, no module graph for the static pipeline to break.
 *
 * Pages with "Ask Arya" buttons fetch the bootstrap once the page is idle, to
 * learn whether the chat is on before showing them; the chat reuses that
 * response. Everything else fetches it only when the chat loads.
 */
(function () {
    'use strict';

    var OFF_KEY = 'arya.off.v1';
    var LIVE_KEY = 'arya.live.v1';
    var NUDGED_KEY = 'arya.nudged.v1';
    var NUDGE_DISMISS_KEY = 'arya.nudge.dismissed.v1';
    var NUDGE_DELAY_MS = 30000;
    var NUDGE_SILENCE_MS = 7 * 24 * 60 * 60 * 1000;
    var SERVICE_PAGE = /^\/(locations\/[^/]+\/[^/]+|services\/[^/]+)\/?$/;

    var html = document.documentElement;
    var rootEl = document.getElementById('floating-contact');
    if (!rootEl) return;

    function read(kind, key) {
        try { return window[kind].getItem(key); } catch (err) { return null; }
    }

    function report(err, where) {
        try {
            if (typeof window.reportClientError === 'function') {
                window.reportClientError(err, { source: 'website_chat.' + where, context: {} });
            }
        } catch (e) { /* ignore */ }
    }

    var offUntil = Number(read('sessionStorage', OFF_KEY) || 0);
    if (offUntil && offUntil > Date.now()) {
        // Switched off a moment ago: today's form, no chat, no Ask buttons.
        html.classList.add('arya-off');
        return;
    }

    var scripts = [];
    try { scripts = JSON.parse(rootEl.getAttribute('data-arya-scripts') || '[]'); } catch (err) { scripts = []; }
    var apiBase = rootEl.getAttribute('data-arya-api') || '/api/website-chat/';
    var toggle = document.getElementById('floating-contact-toggle');
    var panelEl = document.getElementById('floating-contact-panel');
    var loading = null;
    var panel = null;
    var ready = false;
    var settled = false;

    function inject(src) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = src;
            s.async = false;   // keep execution order: copy, state, api, cards, chat
            s.onload = resolve;
            s.onerror = function () { reject(new Error('Arya script failed to load')); };
            document.head.appendChild(s);
        });
    }

    /** Fetch the bundle once and boot the panel. Resolves true when the chat is on. */
    function load() {
        if (loading) return loading;
        loading = Promise.all(scripts.map(inject)).then(function () {
            panel = window.AryaChat && window.AryaChat.boot ? window.AryaChat.boot(rootEl) : null;
            return panel ? panel.ready() : false;
        }).then(function (ok) {
            ready = !!ok;
            settled = true;
            if (!ok) rootEl.classList.remove('is-chat', 'is-loading');
            return ready;
        }).catch(function (err) {
            report(err, 'loader');
            settled = true;
            rootEl.classList.remove('is-chat', 'is-loading');
            return false;
        });
        return loading;
    }

    // ------------------------------------------------- intent on the button

    if (toggle) {
        ['pointerenter', 'focus', 'touchstart', 'pointerdown'].forEach(function (evt) {
            toggle.addEventListener(evt, load, { passive: true, once: true });
        });
    }

    // Capture phase: runs before floating-contact.js opens the panel, so a tap
    // that beats the bundle shows Arya's loading state rather than the form.
    rootEl.addEventListener('click', function (event) {
        if (!toggle || !toggle.contains(event.target) || settled) return;
        var opening = !panelEl || panelEl.getAttribute('aria-hidden') !== 'false';
        if (opening) rootEl.classList.add('is-chat', 'is-loading');
        load();
    }, true);

    // ------------------------------------------------------- Ask Arya

    document.addEventListener('click', function (event) {
        var btn = event.target && event.target.closest ? event.target.closest('[data-arya-ask]') : null;
        if (!btn) return;
        event.preventDefault();
        var context = {
            kind: btn.getAttribute('data-arya-kind') || 'other',
            label: btn.getAttribute('data-arya-label') || '',
            service: btn.getAttribute('data-arya-service') || '',
            text: (btn.textContent || '').trim()
        };
        load().then(function (ok) {
            // After this click has finished bubbling: floating-contact.js closes
            // the panel on any outside click, which this one is.
            window.setTimeout(function () {
                if (ok && panel) panel.askAbout(context);
                else if (toggle && panelEl && panelEl.getAttribute('aria-hidden') !== 'false') toggle.click();
            }, 0);
        });
    });

    function onIdle(fn, delay) {
        var run = function () {
            if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 4000 });
            else window.setTimeout(fn, 1);
        };
        if (document.readyState === 'complete') window.setTimeout(run, delay || 0);
        else window.addEventListener('load', function () { window.setTimeout(run, delay || 0); });
    }

    if (document.querySelector('[data-arya-ask]')) {
        onIdle(function () {
            var path = window.location.pathname;
            var promise = fetch(apiBase + 'bootstrap/?path=' + encodeURIComponent(path), {
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin'
            }).then(function (r) {
                return r.json().then(function (body) {
                    if (!r.ok) throw new Error('bootstrap ' + r.status);
                    return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body;
                });
            });
            window.__aryaBootstrap = { path: path, promise: promise };
            promise.then(function (data) {
                if (data && data.available) html.classList.add('arya-on');
                else html.classList.add('arya-off');
            }).catch(function () { html.classList.add('arya-off'); });
        }, 300);
    }

    // ------------------------------------- a person answering, last page

    if (read('sessionStorage', LIVE_KEY) === '1') {
        onIdle(function () {
            load().then(function (ok) { if (ok && panel) panel.resumeInBackground(); });
        });
    }

    // ---------------------------------------------------------- nudge

    function nudgeAllowed() {
        if (read('sessionStorage', NUDGED_KEY)) return false;
        var dismissed = Number(read('localStorage', NUDGE_DISMISS_KEY) || 0);
        return !(dismissed && Date.now() - dismissed < NUDGE_SILENCE_MS);
    }

    function nudge(reason) {
        if (!nudgeAllowed()) return;
        if (panelEl && panelEl.getAttribute('aria-hidden') === 'false') return;
        load().then(function (ok) { if (ok && panel) panel.maybeNudge(reason); });
    }

    if (nudgeAllowed()) {
        if (SERVICE_PAGE.test(window.location.pathname)) {
            window.setTimeout(function () { nudge('timer'); }, NUDGE_DELAY_MS);
        }
        var fine = false;
        try { fine = window.matchMedia('(pointer: fine) and (min-width: 1024px)').matches; } catch (err) { fine = false; }
        if (fine) {
            var onOut = function (event) {
                if (event.relatedTarget || event.clientY > 0) return;
                document.removeEventListener('mouseout', onOut);
                nudge('exit');
            };
            // Not in the first few seconds: a cursor crossing the top edge on
            // arrival is not someone leaving.
            window.setTimeout(function () { document.addEventListener('mouseout', onOut); }, 8000);
        }
    }

    window.AryaLoader = { load: load };
}());
